export type LocationSearchProfile = "locality" | "mixed";

export type LocationBiasCenter = {
  latitude: number;
  longitude: number;
};

export type LocationSearchPassiveHints = {
  timeZone?: string | null;
  locale?: string | null;
  languages?: string[] | null;
};

export type SearchLocationPlacesOptions = {
  searchProfile?: LocationSearchProfile;
  biasCenter?: LocationBiasCenter;
  limit?: number;
  lang?: string;
  passiveHints?: LocationSearchPassiveHints;
};

const SUPPORTED_OLA_LANGUAGES = new Set([
  "bn",
  "en",
  "gu",
  "hi",
  "kn",
  "ml",
  "mr",
  "or",
  "sa",
  "ta",
  "te",
  "ur",
]);

export function getLocationSearchBrowserHints(): LocationSearchPassiveHints {
  const locale =
    typeof navigator !== "undefined"
      ? navigator.language || undefined
      : undefined;
  const languages =
    typeof navigator !== "undefined" && Array.isArray(navigator.languages)
      ? navigator.languages.filter((language) => Boolean(language))
      : locale
        ? [locale]
        : [];

  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    timeZone = undefined;
  }

  return {
    timeZone,
    locale,
    languages,
  };
}

export function getPreferredLocationSearchLanguage(
  hints?: LocationSearchPassiveHints,
  explicitLanguage?: string,
): string {
  const candidates = [
    explicitLanguage,
    hints?.locale,
    ...(hints?.languages ?? []),
  ].filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );

  for (const candidate of candidates) {
    const normalized = normalizeLanguage(candidate);
    if (normalized && SUPPORTED_OLA_LANGUAGES.has(normalized)) {
      return normalized;
    }
  }

  return "en";
}

export function shouldPreferIndiaFromHints(
  hints?: LocationSearchPassiveHints,
): boolean {
  if (hints?.timeZone === "Asia/Kolkata") {
    return true;
  }

  const candidates = [hints?.locale, ...(hints?.languages ?? [])].filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );

  return candidates.some((candidate) => {
    const normalized = candidate.replace(/_/g, "-");
    const segments = normalized
      .split("-")
      .map((segment) => segment.toUpperCase());
    return segments.includes("IN");
  });
}

function normalizeLanguage(language: string): string | null {
  const trimmed = language.trim();
  if (!trimmed) {
    return null;
  }

  const [baseLanguage] = trimmed.replace(/_/g, "-").split("-");
  return baseLanguage?.toLowerCase() || null;
}
